/* THE LICENCE, AS THE CONSOLE SHOWS IT. Read from GET /v1/usage and nothing else: the meters against the licence
   record, its dates and the quarter. The record holds no buyer kind and no edition, and this model reads no account
   field, so two accounts with the same licence see the same view. */
import type { RunKind, Usage } from '../types.ts'

export type Meter = { id: 'versions' | 'lanes'; label: string; used: number; of: number | null; text: string; note: string }
export type Term = { label: string; value: string }
export type LicenceView = { quarter: string; licensed: boolean; world: string | null; meters: Meter[]; local: Term; terms: Term[]; runs: number }

const of = (used: number, allowed: number | null) => (allowed === null ? `${used} (no license)` : `${used} of ${allowed}`)

export function licenceView(u: Usage): LicenceView {
  const l = u.licence
  return {
    quarter: u.quarter,
    licensed: l !== null,
    world: l ? `${l.world}@${l.world_version}` : null,
    meters: [
      { id: 'versions', label: 'Versions tested this quarter', used: u.evaluation_versions_used, of: u.evaluation_versions_allowed, text: of(u.evaluation_versions_used, u.evaluation_versions_allowed), note: 'A version already tested this quarter tests again without counting twice.' },
      { id: 'lanes', label: 'Training runs open', used: u.train_lanes_open, of: u.lanes_hosted, text: of(u.train_lanes_open, u.lanes_hosted), note: 'Training runs open on this server now; a lane frees when its run closes.' },
    ],
    local: { label: 'Local hosts declared', value: l ? String(l.lanes_local_declared) : 'none' },
    terms: l
      ? [
          { label: 'World', value: `${l.world}@${l.world_version}` },
          { label: 'Term', value: l.term === 'year' ? 'One year' : 'One quarter' },
          { label: 'Starts', value: l.starts },
          { label: 'Ends', value: l.ends },
          ...(u.licence_status && u.licence_status.grace_days > 0 ? [{ label: 'Grace', value: `${u.licence_status.grace_days} ${u.licence_status.grace_days === 1 ? 'day' : 'days'}, to ${u.licence_status.grace_ends}` }] : []),
          { label: 'Quarter', value: u.quarter },
        ]
      : [{ label: 'Quarter', value: u.quarter }],
    runs: u.runs,
  }
}

/** Today as the server reads it for the licence: the UTC date, YYYY-MM-DD. */
export const todayOf = (now: Date = new Date()): string => now.toISOString().slice(0, 10)

/** "2026-Q4" begins its successor on 2027-01-01: the day the quarter's meters reset. */
export function nextQuarterStarts(quarter: string): string | null {
  const m = /^(\d{4})-Q([1-4])$/.exec(quarter)
  if (!m) return null
  const y = Number(m[1])
  const q = Number(m[2])
  return q === 4 ? `${y + 1}-01-01` : `${y}-${String(q * 3 + 1).padStart(2, '0')}-01`
}

export type LicenceStatus = 'in_force' | 'grace' | 'none' | 'expired' | 'not_started' | 'other_world'
export type LicenceState = { status: LicenceStatus; inForce: boolean; title: string; sentence: string }

/** Whether the licence is in force today for this world, by the server's own rule (apps/server/src/hosted/licence.ts inForce). The server's
    reading of the day (`licence_status`) decides when it sent one, since grace is its setting and today is its date; the dates alone decide
    otherwise. In grace the license still opens evaluation and train runs (inForce) and says when it ended and when grace ends. */
export function licenceState(u: Usage, today: string, world: { id: string; version: string } | null = null): LicenceState {
  const l = u.licence
  if (!l) return { status: 'none', inForce: false, title: 'No license', sentence: 'This account has no license on this server, so it opens sample runs only. Ask the operator of this server for one.' }
  if (world && (l.world !== world.id || l.world_version !== world.version)) return { status: 'other_world', inForce: false, title: 'The license is for another world', sentence: `The license is for ${l.world}@${l.world_version}; this server runs ${world.id}@${world.version}. Evaluation and train runs need a license for this world; ask the operator of this server.` }
  const s = u.licence_status
  if (s?.status === 'grace') return { status: 'grace', inForce: true, title: 'In grace', sentence: s.sentence }
  if (s?.status === 'ended') return { status: 'expired', inForce: false, title: 'The license has ended', sentence: s.sentence }
  if (s?.status === 'not_started') return { status: 'not_started', inForce: false, title: 'The license has not started', sentence: s.sentence }
  if (s?.status === 'in_force') return { status: 'in_force', inForce: true, title: 'In force', sentence: s.sentence }
  if (today > l.ends) return { status: 'expired', inForce: false, title: 'The license has ended', sentence: `The license ended on ${l.ends}. Evaluation and train runs are closed until it is renewed; every run and run analysis stays readable. Ask the operator of this server to renew it.` }
  if (today < l.starts) return { status: 'not_started', inForce: false, title: 'The license has not started', sentence: `The license starts on ${l.starts}. Evaluation and train runs open from that day; sample runs are open now.` }
  return { status: 'in_force', inForce: true, title: 'In force', sentence: `In force from ${l.starts} to ${l.ends}.` }
}

export type Openable = { ok: true } | { ok: false; reason: string }

/** Whether this account can open a run of this kind now, and the reason when it cannot. With no usage read yet, the server decides. */
export function canOpen(kind: RunKind, u: Usage | null, today: string, world: { id: string; version: string } | null, opts: { versionTested?: boolean } = {}): Openable {
  if (kind === 'sample' || u === null) return { ok: true }
  const s = licenceState(u, today, world)
  if (!s.inForce) return { ok: false, reason: s.sentence }
  if (kind === 'train' && u.lanes_hosted !== null && u.train_lanes_open >= u.lanes_hosted) return { ok: false, reason: `All ${u.lanes_hosted} hosted lanes are in use. A lane frees when its training run closes: stop one under Runs, or wait for it to finish.` }
  if (kind === 'evaluation' && u.evaluation_versions_allowed !== null && u.evaluation_versions_used >= u.evaluation_versions_allowed && opts.versionTested === false) {
    const next = nextQuarterStarts(u.quarter)
    return { ok: false, reason: `${u.evaluation_versions_used} of ${u.evaluation_versions_allowed} versions are already tested in ${u.quarter}. A version tested this quarter tests again without counting; a new one waits for the meter to reset${next ? ` on ${next}` : ''}.` }
  }
  return { ok: true }
}
